import type { StateCreator } from 'zustand';
import type { VendaState } from '../vendaStore';
import {
  eventoClienteCriado,
  eventoClienteSelecionado,
  eventoClienteTrocado,
} from '../../domain/auditoria/eventos';
import type {
  CadastroSimplificadoInput,
  ClienteVenda,
  OrigemCliente,
  OrigemSelecaoCliente,
} from '../../domain/cliente/clienteVenda';
import {
  participaDaPrecificacao,
  type SnapshotPrecoProduto,
} from '../../domain/precificacao/linha';
import type { ClienteCheckout } from '../../../shared/schemas/cliente.schema';
import type { SessaoUsuario } from '../../../shared/schemas/bootstrap.schema';
import {
  mapClienteCheckoutParaVenda,
  mapClienteDefaultParaVenda,
  mapClienteDoDocumentoParaVenda,
  type ClienteDoDocumento,
} from '../../services/cliente/clienteMapper';

/**
 * Slice do cliente da venda em andamento (feature 005).
 *
 * Combinado no `vendaStore` (Zustand+Immer, **sem `persist`**): o cliente
 * selecionado morre num F5, como o carrinho (AD-006, Constitution VI).
 *
 * Responsabilidade única (Constitution II): **orquestrar** a identificação do
 * cliente — aplicar o snapshot, decidir o evento de auditoria e disparar a
 * reprecificação. Nenhuma regra de preço mora aqui (é do domínio da 003) e
 * nenhuma chamada de rede é construída aqui (é da camada de serviço, injetada
 * por `ClienteDeps`).
 *
 * Este slice **não importa `carrinhoSlice.ts`**: lê `linhas` do estado já
 * combinado e chama `reprecificarPorTrocaDeCliente`, que é a superfície pública
 * que a 003 expõe. É o que preserva a separação exigida pela Constitution II.
 */

export interface ClienteState {
  /** `null` só quando não há cliente default configurado e nada foi escolhido (I1). */
  readonly clienteAtual: ClienteVenda | null;
  /**
   * Interno: decide `CLIENTE_SELECIONADO` vs. `CLIENTE_TROCADO`
   * (`research.md` D9). A pré-seleção automática do default nunca o altera (I3).
   */
  readonly houveEscolhaExplicita: boolean;
}

export interface ClienteDeps {
  /**
   * Mesmo predicado injetado no carrinho (feature 003, D8) — não um segundo.
   * Duas fontes de verdade sobre "quando a venda pode ser mutada" poderiam
   * divergir em silêncio (`research.md` D8, AD-043).
   */
  podeMutarCarrinho(): boolean;
  /**
   * Re-fetch de `GetProduto` para um SKU sob o novo cliente (`research.md` D7).
   *
   * Injetado, e não importado: trocar o cliente muda `Codcliente`/`Listapreco`,
   * parâmetros que só fazem sentido numa nova chamada ao ERP — recalcular
   * localmente reimplementaria a seleção de lista de preço do ERP e violaria a
   * Constitution III.
   */
  buscarSnapshotProduto(
    codigoProduto: string,
    cliente: ClienteVenda,
  ): Promise<SnapshotPrecoProduto>;
  /** Aviso ao operador. Injetado para o slice não importar a lib de toast. */
  avisar?: (mensagem: string) => void;
}

/**
 * O que aconteceu com a tentativa de associar um cliente à venda.
 *
 * `'bloqueado'` e `'inalterado'` são no-ops legítimos, não erros: o primeiro é
 * a invariante I4 (pagamento aprovado), o segundo é reescolher quem já está na
 * venda. A UI precisa dos três para decidir se fecha o modal, avisa, ou não faz
 * nada.
 */
export type ResultadoAplicacaoCliente = 'aplicado' | 'bloqueado' | 'inalterado';

export interface ClienteSlice extends ClienteState {
  /**
   * Pré-seleção automática do cliente default (`FR-004`, AD-032).
   *
   * Roda **sem chamada de rede** (AD-108) e **sem evento de auditoria** (I3):
   * não é ação do operador. Chamada uma única vez, no mesmo call site de
   * `resetarAuditoria` (`abrirSessaoDeVenda`).
   */
  inicializarClientePadrao(sessaoUsuario: SessaoUsuario): void;

  /**
   * Deixa a venda **sem cliente**, sem evento de auditoria.
   *
   * Existe para a recusa de pessoa jurídica (Ajuste SINIEF 11/2025, AD-133):
   * quando a identificação é negada, manter o cliente anterior no campo daria a
   * impressão de que a venda seguiu com ele — o operador precisa ver que não há
   * cliente associado (pedido do usuário, 2026-09-03).
   *
   * Não registra evento porque a recusa não é uma escolha do operador sobre
   * quem é o cliente: é o Checkout dizendo que aquele caminho não existe. Pela
   * mesma razão não mexe em `houveEscolhaExplicita` — a próxima identificação
   * válida continua sendo tratada como a primeira escolha (D9).
   */
  limparCliente(): void;

  /**
   * Associa um cliente já cadastrado à venda (`CLI-01`/`CLI-02`, e `'DAV'` pela
   * importação da feature 006 — AD-115, extensão aditiva sem caso especial).
   *
   * Devolve `Promise<ResultadoAplicacaoCliente>`, e não o `void` do rascunho de
   * `contracts/cliente-domain-api.md`, por dois motivos: a troca com carrinho
   * populado dispara um re-fetch por SKU (D7), inerentemente assíncrono; e o
   * no-op por bloqueio pós-pagamento (I4) precisa ser distinguível pela UI —
   * sem isso, o modal de cadastro fecharia como se a associação tivesse
   * acontecido (achado da revisão, 2026-09-03).
   */
  selecionarCliente(
    cliente: ClienteCheckout,
    origem: OrigemSelecaoCliente,
  ): Promise<ResultadoAplicacaoCliente>;

  /**
   * Associa o cliente de um documento importado **só pelo código e pelo nome
   * que o próprio documento trouxe** (`ClienteNome` de `GetDav`/`CarregarNFCe`,
   * AD-237) — usado quando o `GetCliente` falhou. Mesmas guardas e mesmos
   * eventos de `selecionarCliente`; lista de preço e convênio ficam `null`.
   */
  selecionarClienteDoDocumento(
    cliente: ClienteDoDocumento,
    origem: 'DAV' | 'RASCUNHO',
  ): Promise<ResultadoAplicacaoCliente>;

  /**
   * Cadastro simplificado confirmado (`CLI-03`). Dispara **sempre**
   * `CLIENTE_CRIADO`, nunca `CLIENTE_TROCADO`: criar é ação distinta de
   * substituir (`research.md` D9, AD-061).
   *
   * Uma falha de `postCliente` propaga o erro para a UI tratar e **não** muda
   * `clienteAtual` nem registra evento — o slice nunca fica num estado que
   * afirme um cadastro que o ERP não gravou (`SC-003`).
   */
  cadastrarESelecionarCliente(
    dados: CadastroSimplificadoInput,
    criar: (dados: CadastroSimplificadoInput) => Promise<ClienteCheckout>,
  ): Promise<ResultadoAplicacaoCliente>;
}

const AVISO_CLIENTE_BLOQUEADO =
  'Já há pagamento aprovado nesta venda: o cliente não pode mais ser trocado.';

/**
 * **O cliente da venda é escolhido antes do primeiro item, e não muda depois**
 * (decisão do usuário, 2026-09-10).
 *
 * Esta regra **substitui** a reprecificação por troca de cliente que a 005
 * previa (`FR-012`): não existe mais troca de cliente com carrinho populado, e
 * portanto não existe mais o carrinho que precisava ser reprecificado. O motivo
 * é o que o próprio usuário nomeou — a lista de preço. Cada linha foi
 * precificada pelo ERP sob a `Listapreco`/`Codcliente` de quem estava na venda
 * (AD-033), e trocar o cliente depois obriga a refazer todos os `GetProduto`
 * para repor preço e convênio. Esse caminho existia e era caro: uma chamada por
 * SKU, com a corrida entre duas trocas resolvida por contador de geração, e um
 * desfecho — `AVISO_REPRECIFICACAO_FALHOU` — em que a venda continua na tela com
 * o preço do cliente anterior. Fechar a troca elimina a classe inteira de
 * problema no lugar de administrá-la.
 *
 * A frase nomeia a saída porque ela existe: cancelar os itens (ou limpar a
 * venda) devolve o carrinho ao estado em que o cliente ainda pode ser trocado.
 *
 * Exportada porque `CampoClienteVenda` diz o mesmo motivo **antes** do gesto,
 * no campo e no botão "Identificar" — mesma razão de `AVISO_CONDICAO_COM_PAGAMENTO`
 * ser lida pelo `SeletorCondicaoForma`: duas redações do mesmo motivo divergem
 * no dia em que só uma for revisada.
 */
export const AVISO_CLIENTE_COM_ITEM =
  'Esta venda já tem itens: o cliente não pode mais ser trocado, porque a lista de preço dele já valeu na precificação. Cancele os itens para trocar o cliente.';

/**
 * Por que o cliente da venda não pode ser trocado agora — `null` quando pode.
 *
 * **A única cópia do predicado "a venda já tem item"**: o slice a consulta para
 * recusar a troca (`trocaRecusadaPorItem`) e a tela a consulta para fechar o
 * campo de código, a lupa e o F3 *antes* do gesto (pedido do usuário,
 * 2026-10-08). Linha cancelada não conta — ela saiu da venda.
 */
export function motivoClienteBloqueadoPorItem(
  linhas: readonly { readonly cancelada: boolean }[],
): string | null {
  return linhas.some((linha) => !linha.cancelada) ? AVISO_CLIENTE_COM_ITEM : null;
}

const AVISO_REPRECIFICACAO_FALHOU =
  'O cliente foi trocado, mas os preços do carrinho não puderam ser atualizados. Verifique antes de finalizar.';

export function criarClienteSlice(
  deps: ClienteDeps,
): StateCreator<VendaState, [['zustand/immer', never]], [], ClienteSlice> {
  /** Bloqueio pós-pagamento: no-op com aviso, nunca exceção (I4). */
  function clienteBloqueado(): boolean {
    if (deps.podeMutarCarrinho()) {
      return false;
    }
    deps.avisar?.(AVISO_CLIENTE_BLOQUEADO);
    return true;
  }

  return (set, get) => {
    /**
     * Repõe o preço de cada SKU vivo do carrinho sob o novo cliente.
     *
     * Uma chamada por SKU **distinto**, e só de linhas ativas não-congeladas:
     * linha cancelada saiu da venda e linha congelada (rascunho/DAV) traz o
     * preço do documento de origem, que a troca de cliente não pode alterar
     * (invariantes I2/I3 da feature 003).
     */
    /**
     * Identifica a aplicação de cliente em curso.
     *
     * Duas trocas podem se sobrepor — identificar pelo campo e, com os
     * `GetProduto` ainda em voo, escolher outro cliente na lupa. Sem esta
     * geração, a chamada mais **lenta** gravaria por último: snapshots do
     * cliente que já não é o atual, reprecificados com o convênio do atual —
     * preço errado sem erro nem log (achado da revisão, 2026-09-03).
     */
    let geracaoAplicacao = 0;

    async function reprecificarSob(cliente: ClienteVenda, geracao: number): Promise<void> {
      const vivas = get().linhas.filter(participaDaPrecificacao);
      const skus = [...new Set(vivas.map((linha) => linha.snapshot.codigoProduto))];
      if (skus.length === 0) {
        return;
      }

      let snapshots: SnapshotPrecoProduto[];
      try {
        snapshots = await Promise.all(skus.map((sku) => deps.buscarSnapshotProduto(sku, cliente)));
      } catch {
        // O cliente já trocou e a troca é o que o operador pediu — desfazê-la
        // seria pior. O que não pode acontecer em silêncio é o carrinho seguir
        // com o preço do cliente anterior. Uma troca posterior já em curso é a
        // dona do carrinho agora: nem avisa, nem grava.
        if (geracao === geracaoAplicacao) {
          deps.avisar?.(AVISO_REPRECIFICACAO_FALHOU);
        }
        return;
      }

      if (geracao !== geracaoAplicacao) {
        return;
      }

      // Indexado pelo SKU **pedido**, não pelo `codigoProduto` devolvido: se o
      // ERP resolver o código para outra forma (referência → código reduzido,
      // por exemplo), casar pelo retorno deixaria a linha com o snapshot antigo
      // em silêncio.
      const porSku = new Map(skus.map((sku, indice) => [sku, snapshots[indice]] as const));

      // Substituição parcial, não recipe de rascunho — mesmo motivo do
      // `aplicarLinhas` do carrinho: `SnapshotPrecoProduto` é `readonly` de
      // ponta a ponta (Centavos/Milesimos em tuplas fixas), e o draft do Immer
      // exigiria uma versão mutável do tipo só para gravá-lo.
      set({
        linhas: get().linhas.map((linha) => {
          const novo = participaDaPrecificacao(linha)
            ? porSku.get(linha.snapshot.codigoProduto)
            : undefined;
          return novo === undefined ? linha : { ...linha, snapshot: novo };
        }),
      });

      // A fórmula de preço/desconto de convênio não é reimplementada aqui: o
      // carrinho reaproveita `repricarTodosOsSkus` do domínio puro da 003.
      get().reprecificarPorTrocaDeCliente();
    }

    async function aplicar(
      novo: ClienteVenda,
      registrar: (anterior: ClienteVenda | null) => void,
    ): Promise<void> {
      const anterior = get().clienteAtual;
      geracaoAplicacao += 1;
      const geracao = geracaoAplicacao;

      set((state) => {
        state.clienteAtual = novo;
        state.houveEscolhaExplicita = true;
      });

      registrar(anterior);
      await reprecificarSob(novo, geracao);
    }

    /**
     * A troca pedida pelo **operador** com o carrinho já populado
     * (`AVISO_CLIENTE_COM_ITEM`).
     *
     * Linha cancelada não conta: ela saiu da venda, e o cliente volta a ser
     * trocável — é justamente a saída que a frase nomeia.
     *
     * **A importação não passa por aqui.** `'DAV'` e `'RASCUNHO'` chegam de
     * `importarVendaExistente`, que insere as linhas congeladas **antes** de
     * associar o cliente do documento: para elas a venda inteira está sendo
     * substituída pelo conteúdo do ERP, e não há cliente da venda corrente a
     * proteger. É a mesma distinção que `trocarVendedor` faz ao sobrescrever
     * incondicionalmente. Bloquear as duas deixaria todo DAV importado com o
     * cliente default, calado.
     */
    function trocaRecusadaPorItem(origem: OrigemCliente): boolean {
      if (origem === 'DAV' || origem === 'RASCUNHO') {
        return false;
      }
      const motivo = motivoClienteBloqueadoPorItem(get().linhas);
      if (motivo === null) {
        return false;
      }
      deps.avisar?.(motivo);
      return true;
    }

    /**
     * Associação de um cliente já montado — o caminho comum de
     * `selecionarCliente` (cadastro do `GetCliente`) e de
     * `selecionarClienteDoDocumento` (só o nome do documento, AD-237). Os dois
     * diferem apenas em **de onde** vem o `ClienteVenda`.
     */
    async function associar(
      novo: ClienteVenda,
      origem: OrigemSelecaoCliente,
    ): Promise<ResultadoAplicacaoCliente> {
      if (clienteBloqueado()) {
        return 'bloqueado';
      }

      // Reescolher o cliente que já está na venda não é troca: registrar
      // `CLIENTE_TROCADO` com anterior === novo mandaria ao ERP, no log de
      // `FaturarNFCe`, uma troca que não aconteceu (`FR-015`), e o re-fetch
      // rebuscaria todos os SKUs para chegar ao mesmo preço.
      if (get().clienteAtual?.codigoCliente === novo.codigoCliente) {
        return 'inalterado';
      }

      // A recusa por item vem **depois** da guarda acima: reescolher quem já
      // está na venda não muda cliente nenhum, e avisar ali que "o cliente
      // não pode mais ser trocado" alarmaria o operador por um gesto que não
      // trocaria nada.
      if (trocaRecusadaPorItem(origem)) {
        return 'bloqueado';
      }

      const primeiraEscolha = !get().houveEscolhaExplicita;

      await aplicar(novo, (anterior) => {
        // Sem escolha explícita anterior, o que existia era o default
        // silencioso (ou nada): a primeira interação do operador é uma
        // *seleção*, não uma troca (D9).
        if (primeiraEscolha || anterior === null) {
          get().registrarEventoAuditoria(
            eventoClienteSelecionado({ codigoCliente: novo.codigoCliente, nome: novo.nome }),
          );
          return;
        }
        get().registrarEventoAuditoria(
          eventoClienteTrocado({
            codigoClienteAnterior: anterior.codigoCliente,
            codigoClienteNovo: novo.codigoCliente,
          }),
        );
      });

      return 'aplicado';
    }

    return {
      clienteAtual: null,
      houveEscolhaExplicita: false,

      limparCliente: () => {
        set((state) => {
          state.clienteAtual = null;
        });
      },

      inicializarClientePadrao: (sessaoUsuario) => {
        set((state) => {
          state.clienteAtual = mapClienteDefaultParaVenda(sessaoUsuario);
          state.houveEscolhaExplicita = false;
        });
      },

      selecionarCliente: (cliente, origem) =>
        associar(mapClienteCheckoutParaVenda(cliente, origem), origem),

      selecionarClienteDoDocumento: (cliente, origem) =>
        associar(mapClienteDoDocumentoParaVenda(cliente, origem), origem),

      cadastrarESelecionarCliente: async (dados, criar) => {
        // A recusa vem **antes** de `criar`: cadastrar no ERP um cliente que a
        // venda não pode receber deixaria um cadastro órfão a cada tentativa.
        if (clienteBloqueado() || trocaRecusadaPorItem('CADASTRO_SIMPLIFICADO')) {
          return 'bloqueado';
        }

        // Fora de qualquer `set`: enquanto o ERP não confirmar a criação, o
        // estado da venda não muda (`SC-003`). Um erro aqui sobe para a UI.
        const criado = await criar(dados);
        const novo = mapClienteCheckoutParaVenda(criado, 'CADASTRO_SIMPLIFICADO');

        await aplicar(novo, () => {
          get().registrarEventoAuditoria(
            eventoClienteCriado({ codigoCliente: novo.codigoCliente, nome: novo.nome }),
          );
        });

        return 'aplicado';
      },
    };
  };
}
